import { AppFeatures } from '@feature-flags';
import { BehaviorSubject } from 'rxjs';

import { Injectable, inject } from '@angular/core';

import { FeatureFlagsService } from '../../../services';

export type PromotionalDialogConfig = {
  title: string;
  description: string;
  storageKey: string;
  featureFlag?: keyof AppFeatures | null;
  isVisible: boolean;
  severity: 'info' | 'success' | 'warning' | 'error' | 'notification';
  /** `null` means the dialog never expires. */
  expirationDate: Date | null;
  isTemporary: boolean;
  hasDownloadButton: boolean;
  downloadURL?: string;
  downloadManagementId?: string;
};

@Injectable({
  providedIn: 'root',
})
export class PromotionalDialogService {
  private readonly dialogs: PromotionalDialogConfig[] = [];
  private readonly dialogsSubject = new BehaviorSubject<
    PromotionalDialogConfig[]
  >([]);
  dialogs$ = this.dialogsSubject.asObservable();
  featureFlagsService = inject(FeatureFlagsService);

  constructor() {
    const defaultDialog: PromotionalDialogConfig = {
      title: 'The Command Palette',
      description: `Power user? Navigate Phoenix faster with <strong> Cmd + K</strong> or <strong>Ctrl + K</strong>`,
      storageKey: 'command-palette-dialog',
      featureFlag: 'show_cmdk_command_dialog',
      isVisible: false,
      severity: 'info',
      expirationDate: new Date('2025-07-01'),
      isTemporary: false,
      hasDownloadButton: false,
    };

    this.addDialog(defaultDialog);
  }

  /**
   * Adds a dialog, or replaces the existing one with the same `storageKey`.
   * `storageKey` identifies a dialog, so duplicates would stack up visually and
   * leave `closeDialog` — which matches on the first entry only — unable to
   * dismiss them all.
   */
  addDialog(dialogConfig: PromotionalDialogConfig): void {
    const newDialog: PromotionalDialogConfig = {
      ...dialogConfig,
      isVisible: this.setVisibility(dialogConfig),
    };
    const existingIndex = this.dialogs.findIndex(
      dialog => dialog.storageKey === newDialog.storageKey
    );
    if (existingIndex > -1) {
      this.dialogs[existingIndex] = newDialog;
    } else {
      this.dialogs.push(newDialog);
    }
    this.dialogsSubject.next(this.dialogs);
  }

  private setVisibility(dialog: PromotionalDialogConfig): boolean {
    const isHiddenInStorage =
      localStorage.getItem(dialog.storageKey) === 'hidden';
    const isExpired =
      !!dialog.expirationDate && dialog.expirationDate < new Date();

    if (isHiddenInStorage || isExpired) return false;

    if (dialog.featureFlag) {
      return this.featureFlagsService.isOn(dialog.featureFlag);
    }

    return true;
  }

  closeDialog(storageKey: string): void {
    const dialogIndex = this.dialogs.findIndex(
      d => d.storageKey === storageKey
    );
    if (dialogIndex > -1) {
      if (this.dialogs[dialogIndex].isTemporary) {
        this.dialogs.splice(dialogIndex, 1);
      } else {
        this.dialogs[dialogIndex].isVisible = false;
        localStorage.setItem(this.dialogs[dialogIndex].storageKey, 'hidden');
      }
      this.dialogsSubject.next(this.dialogs);
    }
  }

  areAllDialogsHidden(): boolean {
    return this.dialogs.every(dialog => !dialog.isVisible);
  }
}
